import React, { useState, useCallback, KeyboardEvent } from 'react';
import {
  Box,
  VStack,
  Input,
  Image,
  Flex,
  useToast,
  Text,
  Button,
  Icon,
  FormControl,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Link as ChakraLink
} from "@chakra-ui/react";
import { FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link as RouterLink } from 'react-router-dom';
import colors from "../../theme/foundation/colors";
import { resetPassword } from 'aws-amplify/auth';
import { confirmResetPassword } from 'aws-amplify/auth';
import { SIGNUP_ROUTE } from '../../utils/constants/routes';

interface LoginProvider {
  id: string;
  value: string;
}
import ALJLogin from "../../assets/ALJ-login.png";
interface LoginComponentProps {
  onLogin: (providerValue: string, username?: string, password?: string) => void;
  error: string | null;
}

const loginProviders: LoginProvider[] = [
  { id: "Users with ALJ.com Email", value: "azureadidp2" },
  { id: "Users with ALJ.ae Email", value: "azureadidp1" },
  { id: "Users with Other Email", value: "cognito" },
];

const LoginComponent: React.FC<LoginComponentProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const toast = useToast();
  const [showCredentials, setShowCredentials] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState<'INITIAL' | 'CONFIRM_CODE'>('INITIAL');
  const [showOtherEmail, setShowOtherEmail] = useState(false);
  const [hasClickedOtherEmail, setHasClickedOtherEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const isFormValid = useCallback(() => {
    return username.trim() !== '' && password.trim() !== '' && !usernameError;
  }, [username, password, usernameError]);

  const validateUsername = (value: string) => {
    setUsernameError('');
    setUsername(value);
  };

  const handleSignIn = async () => {
    setLocalError(null);

    if (isFormValid()) {
      try {
          setIsLoading(true);
        const lowercaseUsername = username.toLowerCase();

        // ✅ 1. Call Lambda to check user existence
        const response = await fetch(
          `https://mbbvjpxvbjvpk3e73p3bu4uu5u0xkayj.lambda-url.eu-west-1.on.aws/?username=${lowercaseUsername}`
        );
        const data = await response.json();

        // ✅ 2. Normalize response
        let userExists = false;

        if (data.exists !== undefined) {
          userExists = data.exists;
        } else if (data.body) {
          const parsedBody = JSON.parse(data.body);
          userExists = parsedBody.exists;
        }

        // ✅ 3. If user does NOT exist → STOP login
        if (!userExists) {
          setLocalError(`User does not exist. Please sign up first. (${Date.now()})`);
          setIsLoading(false);
          return;
        }

        await onLogin('cognito', lowercaseUsername, password);
      } catch (error) {
        console.log("Catching error in CustomLoginPage:", error);
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'An unknown error occurred during sign-in.';
        
        setLocalError(`${errorMessage} (${Date.now()})`);
        setIsLoading(false);
      }
    }
  };

  const handleProviderSignIn = (provider: LoginProvider) => {
    if (provider.value === 'cognito') {
      setShowCredentials(!showCredentials);
      setShowOtherEmail(true);
      setHasClickedOtherEmail(true);
    } else {
      onLogin(provider.value);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isFormValid()) {
      handleSignIn();
    }
  };

  React.useEffect(() => {
    console.log("Current localError state:", localError);
  }, [localError]);

  React.useEffect(() => {
    if (localError) {
      console.log("Attempting to show toast for:", localError);
      toast({
        title: "Error",
        description: localError.split(' (')[0],
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  }, [localError, toast]);

  const handleResetPassword = async () => {
      if (!resetEmail) return;
    try {
        setIsResetLoading(true);
        const lowercaseEmail = resetEmail.toLowerCase();

        // ✅ 1. Call Lambda to check user existence
        const response = await fetch(
          `https://mbbvjpxvbjvpk3e73p3bu4uu5u0xkayj.lambda-url.eu-west-1.on.aws/?username=${lowercaseEmail}`
        );

        const data = await response.json();
        // ✅ 2. Normalize response
        let userExists = false;


        if (data.exists !== undefined) {
          userExists = data.exists;
        } else if (data.body) {
          const parsedBody = JSON.parse(data.body);
          userExists = parsedBody.exists;
        }


        // ✅ 3. If user does NOT exist → show error
        if (!userExists) {
          toast({
            title: "User Not Found",
            description: "No account found with this email. Please sign up first.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          return;
        }
        setIsResetLoading(false);


        await resetPassword({ username: lowercaseEmail });
      setResetStep('CONFIRM_CODE');
      toast({
        title: "Code Sent",
        description: "A verification code has been sent to your email",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send reset code",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }finally {
    setIsResetLoading(false); // ✅ STOP LOADER ALWAYS
  }

  };

  const handleConfirmReset = async () => {
    try {
        setIsConfirmLoading(true);
      const lowercaseEmail = resetEmail.toLowerCase();
      await confirmResetPassword({
        username: lowercaseEmail,
        confirmationCode: resetCode,
        newPassword: newPassword,
      });
      toast({
        title: "Success",
        description: "Password has been reset successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setIsResetPassword(false);
      setResetStep('INITIAL');
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reset password",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }finally {
    setIsConfirmLoading(false); // ✅ STOP LOADER ALWAYS
  }

  };

  return (
    <Flex height="100vh" width="100vw" bg={colors.backgroundMain}>
      <Box width="55%" bg={colors.teal} />
      <Box width="45%" bg="gray.100" />
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
      >
        <Box 
          width={["90%", "400px"]}
          borderRadius="lg" 
          boxShadow="xl" 
          bg="white" 
          p={8}
        >
          <VStack spacing={6} align="stretch">
            <Flex justify="center" align="center" mb={1}>
              <Image src={ALJLogin} alt="Abdul Latif Jameel" height="50px" />
            </Flex>
            
            <VStack spacing={0} align="center" mb={1}>
              <Text fontSize="md" color={colors.slate} textAlign="center">
                Please select login below based on your email
              </Text>
            </VStack>
            
            <VStack spacing={showOtherEmail ? 0 : 4} align="stretch">
              {loginProviders.map((provider, index) => (
                <Box key={provider.id} width="100%">
                  {(index < 2 && !showOtherEmail) && (
                    <Button
                      onClick={() => handleProviderSignIn(provider)}
                      size="lg"
                      height="56px"
                      variant="outline"
                      borderWidth="2px"
                      borderColor="gray.300"
                      width="100%"
                      _hover={{
                        bg: "gray.50",
                        borderColor: "teal.500",
                        transform: "translateY(-2px)",
                        boxShadow: "md"
                      }}
                      _active={{
                        bg: "gray.100",
                        transform: "translateY(0)",
                      }}
                      transition="all 0.2s"
                    >
                      <Flex align="center" width="100%" justify="center">
                        <Icon 
                          as={FaEnvelope} 
                          mr={3} 
                          color={colors.darkteal} 
                          boxSize={5}
                        />
                        <Text color={colors.darkteal} fontSize="md" fontWeight="medium">
                          {provider.id}
                        </Text>
                      </Flex>
                    </Button>
                  )}
                  {index === 2 && (
                    <>
                      <Button
                        onClick={() => handleProviderSignIn(provider)}
                        size="lg"
                        height="56px"
                        variant="outline"
                        borderWidth="2px"
                        borderColor="gray.300"
                        width="100%"
                        isDisabled={hasClickedOtherEmail && showCredentials}
                        _hover={{
                          bg: "gray.50",
                          borderColor: "teal.500",
                          transform: "translateY(-2px)",
                          boxShadow: "md"
                        }}
                        _active={{
                          bg: "gray.100",
                          transform: "translateY(0)",
                        }}
                        _disabled={{
                          bg: "white",
                          opacity: 1,
                          cursor: "not-allowed",
                          borderColor: "gray.300"
                        }}
                        transition="all 0.2s"
                      >
                        <Flex align="center" width="100%" justify="center">
                          <Icon 
                            as={FaEnvelope} 
                            mr={3} 
                            color={colors.darkteal} 
                            boxSize={5}
                          />
                          <Text color={colors.darkteal} fontSize="md" fontWeight="medium">
                            {provider.id}
                          </Text>
                        </Flex>
                      </Button>
                      {showCredentials && (
                        <Box mt={4} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.300" bg="gray.50">
                          <VStack spacing={4}>
                            {!isResetPassword ? (
                              <>
                                <FormControl isInvalid={!!usernameError}>
                                  <Input 
                                    placeholder="Username" 
                                    value={username} 
                                    onChange={(e) => validateUsername(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    size="lg"
                                    height="56px"
                                    borderWidth="2px"
                                    _focus={{
                                      borderColor: "teal.500",
                                      boxShadow: "0 0 0 1px teal.500"
                                    }}
                                  />
                                  <FormErrorMessage>{usernameError}</FormErrorMessage>
                                </FormControl>
                                
                                <FormControl>
                                  <InputGroup size="lg">
                                    <Input 
                                      type={showPassword ? "text" : "password"}
                                      placeholder="Password" 
                                      value={password} 
                                      onChange={(e) => setPassword(e.target.value)}
                                      onKeyPress={handleKeyPress}
                                      height="56px"
                                      borderWidth="2px"
                                      _focus={{
                                        borderColor: "teal.500",
                                        boxShadow: "0 0 0 1px teal.500"
                                      }}
                                    />
                                    <InputRightElement width="4.5rem" height="100%">
                                      <Button
                                        h="1.75rem"
                                        size="sm"
                                        onClick={() => setShowPassword(!showPassword)}
                                        variant="ghost"
                                      >
                                        <Icon as={showPassword ? FaEyeSlash : FaEye} />
                                      </Button>
                                    </InputRightElement>
                                  </InputGroup>
                                </FormControl>
                                
                                <Button
                                  onClick={handleSignIn}
                                  isLoading={isLoading}
                                  isDisabled={!isFormValid() || isLoading}
                                  size="lg"
                                  height="56px"
                                  width="100%"
                                  backgroundColor="#006778"
                                  color="white"
                                  _hover={{
                                    backgroundColor: "#005666",
                                    transform: "translateY(-2px)",
                                    boxShadow: "md"
                                  }}
                                  _active={{
                                    backgroundColor: "#004555",
                                    transform: "translateY(0)",
                                  }}
                                  _disabled={{
                                    backgroundColor: "#006778",
                                    opacity: 0.6,
                                    cursor: "not-allowed"
                                  }}
                                  transition="all 0.2s"
                                >
                                  SIGN IN
                                </Button>
                                
                                <ChakraLink 
                                  color="teal.600" 
                                  onClick={() => setIsResetPassword(true)}
                                  fontSize="sm" 
                                  textAlign="center"
                                  cursor="pointer"
                                  _hover={{ color: "teal.700", textDecoration: "underline" }}
                                >
                                  Forgot your password?
                                </ChakraLink>
                              </>
                            ) : (
                              <>
                                {resetStep === 'INITIAL' ? (
                                  <VStack spacing={4} width="100%">
                                    <FormControl>
                                      <Input
                                        placeholder="Enter your email"
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                        size="lg"
                                        height="56px"
                                      />
                                    </FormControl>
                                    
                                    <Button
                                      onClick={handleResetPassword}
                                      isLoading={isResetLoading}
                                      size="lg"
                                      height="56px"
                                      width="100%"
                                      backgroundColor="#006778"
                                      color="white"
                                      isDisabled={!resetEmail || isResetLoading}
                                    >
                                      Send Reset Code
                                    </Button>
                                  </VStack>
                                ) : (
                                  <VStack spacing={4} width="100%">
                                    <FormControl>
                                      <Input
                                        placeholder="Enter verification code"
                                        value={resetCode}
                                        onChange={(e) => setResetCode(e.target.value)}
                                        size="lg"
                                        height="56px"
                                      />
                                    </FormControl>
                                    
                                    <FormControl>
                                      <InputGroup size="lg">
                                        <Input
                                          type={showPassword ? "text" : "password"}
                                          placeholder="Enter new password"
                                          value={newPassword}
                                          onChange={(e) => setNewPassword(e.target.value)}
                                          height="56px"
                                        />
                                        <InputRightElement width="4.5rem" height="100%">
                                          <Button
                                            h="1.75rem"
                                            size="sm"
                                            onClick={() => setShowPassword(!showPassword)}
                                            variant="ghost"
                                          >
                                            <Icon as={showPassword ? FaEyeSlash : FaEye} />
                                          </Button>
                                        </InputRightElement>
                                      </InputGroup>
                                    </FormControl>
                                    
                                    <Button
                                      onClick={handleConfirmReset}
                                      isLoading={isConfirmLoading}
                                      size="lg"
                                      height="56px"
                                      width="100%"
                                      backgroundColor="#006778"
                                      color="white"
                                      isDisabled={!resetCode || !newPassword}
                                    >
                                      Reset Password
                                    </Button>
                                  </VStack>
                                )}
                                
                                <ChakraLink
                                  color="teal.600"
                                  onClick={() => {
                                    setIsResetPassword(false);
                                    setResetStep('INITIAL');
                                    setResetEmail('');
                                    setResetCode('');
                                    setNewPassword('');
                                  }}
                                  fontSize="sm"
                                  textAlign="center"
                                  cursor="pointer"
                                  _hover={{ color: "teal.700", textDecoration: "underline" }}
                                >
                                  Back to Login
                                </ChakraLink>
                              </>
                            )}
                          </VStack>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </VStack>

            {showOtherEmail && (
              <ChakraLink
                color="teal.600"
                onClick={() => {
                  setShowOtherEmail(false);
                  setShowCredentials(false);
                  setUsername('');
                  setPassword('');
                }}
                fontSize="sm"
                textAlign="center"
                cursor="pointer"
                _hover={{ color: "teal.700", textDecoration: "underline" }}
                mt={4}
                display="block"
                width="100%"
              >
                Back to all login options
              </ChakraLink>
            )}
            
            {!showOtherEmail && (
              <Flex justify="center" mt={4}>
                <Text fontSize="sm" color="gray.600">
                  Don't have an account?{" "}
                  <ChakraLink
                    as={RouterLink}
                    to={SIGNUP_ROUTE}
                    color="teal.600"
                    _hover={{ color: "teal.700", textDecoration: "underline" }}
                  >
                    Sign up
                  </ChakraLink>
                </Text>
              </Flex>
            )}
          </VStack>
        </Box>
      </Flex>
    </Flex>
  );
};

export default LoginComponent;
